import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import * as Yup from 'yup'

import {
  Alert,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { TypedFormik } from '@dai/components'

export type PasswordResetFormLabels = {
  mustBeValidEmail: string
  emailRequired: string
  email: string
  resetPasswordButton: string
  emailSentConfirmationMessage: (email: string) => string
  defaultErrorMessage: string
  dontHaveAnAccount: string
  signUpButton: string
}

interface SendResetPasswordEmailProps {
  signUpUrl: string
  sendPasswordResetEmail: (email: string) => Promise<void>
  labels: PasswordResetFormLabels
}

const SendResetPasswordEmail: React.FC<SendResetPasswordEmailProps> = ({
  signUpUrl,
  sendPasswordResetEmail,
  labels,
}) => {
  return (
    <TypedFormik
      initialValues={{
        email: '',
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email(labels.mustBeValidEmail)
          .max(255)
          .required(labels.emailRequired),
      })}
      onSubmit={async (values, { setStatus }) => {
        try {
          await sendPasswordResetEmail(values.email)
          setStatus({
            success: true,
            message: labels.emailSentConfirmationMessage(values.email),
          })
        } catch (error: any) {
          const message = error.message || labels.defaultErrorMessage
          setStatus({ success: false, message })
        }
      }}
    >
      {({
        errors,
        status,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {status?.message && (
              <Alert severity={status.success ? 'success' : 'warning'}>
                {status.message}
              </Alert>
            )}
            <TextField
              type="email"
              name="email"
              label={labels.email}
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              loading={isSubmitting}
              loadingPosition="end"
            >
              {labels.resetPasswordButton}
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              {labels.dontHaveAnAccount}
              <Link
                to={signUpUrl}
                component={RouterLink}
                style={{ marginLeft: 4 }}
              >
                {labels.signUpButton}
              </Link>
            </Typography>
          </Stack>
        </form>
      )}
    </TypedFormik>
  )
}

export default SendResetPasswordEmail
